/**
 * Billing Routes
 * Routes for fees, invoices, payments, discounts, and statements
 */

import { lazy } from "react";
import { RouteObject } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import {
  ParentOnStaffInvoice,
  ParentOnStaffStatement,
  ParentOnStaffStatements,
} from "@/components/auth/EmailLinkRedirects";
import { AppShell } from "@/components/layout/AppShell";
import { LazyPage, RedirectKeepingState } from "../helpers";

// Fee Types
const FeeTypesListPage = lazy(() => import("@/pages/feeTypes/FeeTypesListPage"));
const FeeTypeFormPage = lazy(() => import("@/pages/feeTypes/FeeTypeFormPage"));
const FeeTypeDetailPage = lazy(() => import("@/pages/feeTypes/FeeTypeDetailPage"));

// Invoices
const InvoicesListPage = lazy(() => import("@/pages/invoices/InvoicesListPage"));
const InvoiceDetailPage = lazy(() => import("@/pages/invoices/InvoiceDetailPage"));
const GenerateInvoicePage = lazy(() => import("@/pages/invoices/GenerateInvoicePage"));
const BatchGenerateInvoicesPage = lazy(() => import("@/pages/invoices/BatchGenerateInvoicesPage").then(m => ({ default: m.BatchGenerateInvoicesPage })));

// Payments
const PaymentsListPage = lazy(() => import("@/pages/payments/PaymentsListPage"));
const RecordPaymentPage = lazy(() => import("@/pages/payments/RecordPaymentPage"));
const PaymentDetailPage = lazy(() => import("@/pages/payments/PaymentDetailPage"));

// Discounts
const DiscountsListPage = lazy(() => import("@/pages/discounts").then(m => ({ default: m.DiscountsListPage })));
const DiscountFormPage = lazy(() => import("@/pages/discounts").then(m => ({ default: m.DiscountFormPage })));
const DiscountDetailPage = lazy(() => import("@/pages/discounts").then(m => ({ default: m.DiscountDetailPage })));
const PendingApprovalsPage = lazy(() => import("@/pages/discounts").then(m => ({ default: m.PendingApprovalsPage })));

// Zakat Fund
const ZakatFundPage = lazy(() => import("@/pages/discounts/ZakatFundPage"));

// Sponsor Fund (TEFA sponsor funding, mig 163)
const SponsorFundPage = lazy(() => import("@/pages/discounts/SponsorFundPage"));

// Statements
const StatementsListPage = lazy(() => import("@/pages/statements/StatementsListPage").then(m => ({ default: m.StatementsListPage })));
const StatementDetailPage = lazy(() => import("@/pages/statements/StatementDetailPage").then(m => ({ default: m.StatementDetailPage })));
const GenerateStatementPage = lazy(() => import("@/pages/statements/GenerateStatementPage").then(m => ({ default: m.GenerateStatementPage })));

export const billingRoutes: RouteObject[] = [
  // Fee Type Routes
  {
    path: "/fees",
    element: <RedirectKeepingState to="/fees/types" />,
  },
  {
    path: "/fees/types",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><FeeTypesListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/fees/types/new",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><FeeTypeFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/fees/types/:id/edit",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><FeeTypeFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/fees/types/:id",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><FeeTypeDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Invoice Routes
  {
    path: "/invoices",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><InvoicesListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/invoices/generate",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><GenerateInvoicePage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/invoices/batch",
    element: (
      <ProtectedRoute roles={["super_user", "admin"]}>
        <AppShell>
          <LazyPage><BatchGenerateInvoicesPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/invoices/:id",
    element: (
      <ParentOnStaffInvoice>
        <ProtectedRoute permission="manage:fees">
          <AppShell>
            <LazyPage><InvoiceDetailPage /></LazyPage>
          </AppShell>
        </ProtectedRoute>
      </ParentOnStaffInvoice>
    ),
  },

  // Payment Routes
  {
    path: "/payments",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><PaymentsListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/payments/record",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><RecordPaymentPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/payments/:id",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><PaymentDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Discount Routes
  {
    path: "/discounts",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><DiscountsListPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/discounts/new",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><DiscountFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/discounts/approvals",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><PendingApprovalsPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/discounts/:id/edit",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><DiscountFormPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
  {
    path: "/discounts/:id",
    element: (
      <ProtectedRoute permission="manage:fees">
        <AppShell>
          <LazyPage><DiscountDetailPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Zakat Fund
  {
    path: "/zakat-fund",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal"]}>
        <AppShell>
          <LazyPage><ZakatFundPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Sponsor Fund (the page itself renders a notice for non-sponsor tenants)
  {
    path: "/sponsor-fund",
    element: (
      <ProtectedRoute roles={["super_user", "admin", "principal"]}>
        <AppShell>
          <LazyPage><SponsorFundPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },

  // Statement Routes
  {
    path: "/statements",
    element: (
      <ParentOnStaffStatements>
        <ProtectedRoute roles={["super_user", "admin"]}>
          <AppShell>
            <LazyPage><StatementsListPage /></LazyPage>
          </AppShell>
        </ProtectedRoute>
      </ParentOnStaffStatements>
    ),
  },
  {
    path: "/statements/:statementId",
    element: (
      <ParentOnStaffStatement>
        <ProtectedRoute roles={["super_user", "admin"]}>
          <AppShell>
            <LazyPage><StatementDetailPage /></LazyPage>
          </AppShell>
        </ProtectedRoute>
      </ParentOnStaffStatement>
    ),
  },
  {
    path: "/statements/generate",
    element: (
      <ProtectedRoute roles={["super_user", "admin"]}>
        <AppShell>
          <LazyPage><GenerateStatementPage /></LazyPage>
        </AppShell>
      </ProtectedRoute>
    ),
  },
];
